/**
 * Parliamentary Motion Effects System
 * Handles automatic enforcement of passed subsidiary motions per Robert's Rules
 */

import { MOTION_TYPES, MOTION_CLASSES } from './motionTypes.js';

/**
 * Process the result of a motion and apply appropriate parliamentary effects
 * @param {Object} motion - The motion that was voted on
 * @param {string} result - 'passed' or 'failed'
 * @param {Object} meetingState - Current meeting state
 * @returns {Object} Updated meeting state with effects applied
 */
export const processMotionResult = (motion, result, meetingState) => {
  if (result !== 'passed') {
    return meetingState; // No effects for failed motions
  }

  const effects = getMotionEffects(motion.type);
  if (!effects) {
    return meetingState; // No special effects for this motion type
  }

  return effects.execute(motion, meetingState);
};

/**
 * Get the effect handler for a specific motion type
 * @param {string} motionType - The type of motion (PREVQ, TABLE, etc.)
 * @returns {Object|null} Effect handler or null if no special effects
 */
const getMotionEffects = (motionType) => {
  const effectHandlers = {
    'PREVQ': previousQuestionEffects,
    'TABLE': tableMotionEffects,
    'POSTPONE': postponeMotionEffects,
    'COMMIT': referToCommitteeEffects, // Updated from 'REFER' to 'COMMIT'
    'POSTPONE_INDEF': postponeIndefinitelyEffects,
    'LIMIT_DEBATE': limitDebateEffects,
    'ADJ': adjournEffects,
    'RECESS': recessEffects,
    // Incidental motions with effects
    'WITHDRAW_MOTION': withdrawMotionEffects,
    'DIVISION_QUESTION': divisionQuestionEffects,
    'SUSPEND_RULES': suspendRulesEffects,
    // Bring-back motions
    'TAKE_FROM_TABLE': takeFromTableEffects,
    'RECONSIDER': reconsiderEffects,
    'RESCIND': rescindEffects,
    'RATIFY': ratifyEffects
  };

  return effectHandlers[motionType] || null;
};

/**
 * Previous Question (Close Debate) Effects
 * RONR: Immediately closes debate and advances to vote on pending question
 */
const previousQuestionEffects = {
  execute: (motion, meetingState) => {
    console.log('🔨 Previous Question passed - closing debate and advancing to vote');

    const { currentMotion, motionStack, meetingMinutes } = meetingState;

    // Find the main motion or pending question
    const pendingMotion = currentMotion?.type === 'MAIN' ? currentMotion :
                         motionStack.find(m => m.type === 'MAIN');

    if (!pendingMotion) {
      console.warn('Previous Question passed but no pending question found');
      return meetingState;
    }

    // Close debate and advance to vote
    const updatedMotion = {
      ...pendingMotion,
      status: 'ready_for_vote',
      debateClosed: true,
      debateClosedBy: 'previous_question',
      debateClosedAt: new Date().toLocaleTimeString()
    };

    // Add minute entry for debate closure
    const newMinute = {
      id: Date.now() + '_debate_closed',
      type: 'motion_effect',
      text: 'Debate closed by Previous Question - proceeding immediately to vote',
      timestamp: new Date().toLocaleTimeString(),
      chronologicalOrder: (meetingMinutes[meetingMinutes.length - 1]?.chronologicalOrder || 0) + 1,
      effectType: 'previous_question',
      status: 'completed'
    };

    return {
      ...meetingState,
      currentMotion: currentMotion?.type === 'MAIN' ? updatedMotion : currentMotion,
      motionStack: motionStack.map(m => m.type === 'MAIN' ? updatedMotion : m),
      meetingMinutes: [...meetingMinutes, newMinute],
      autoAdvanceToVote: true // Signal to UI to advance to vote
    };
  }
};

/**
 * Lay on the Table Effects
 * RONR: Immediately disposes of pending business until taken from table
 */
const tableMotionEffects = {
  execute: (motion, meetingState) => {
    console.log('📋 Lay on Table passed - disposing of pending business');

    const { currentMotion, motionStack, meetingMinutes, tabledMotions = [] } = meetingState;

    // Find what to table (current motion or main motion)
    const motionToTable = currentMotion?.type === 'MAIN' ? currentMotion :
                         motionStack.find(m => m.type === 'MAIN');

    if (!motionToTable) {
      console.warn('Table motion passed but no business to table');
      return meetingState;
    }

    // Create tabled motion record
    const tabledMotion = {
      ...motionToTable,
      status: 'tabled',
      tabledAt: new Date().toLocaleTimeString(),
      tabledDate: new Date().toLocaleDateString(),
      originalStack: motionStack.filter(m => m.type !== 'TABLE'), // Preserve subsidiary motions
      id: motionToTable.id || Date.now()
    };

    // Update the existing minute entry for the main motion to show it was tabled
    // and ensure the Table motion itself is properly marked as passed
    const updatedMinutes = meetingMinutes.map(entry => {
      // Update main motion to show it was tabled
      if (entry.id === `motion_${motionToTable.id}`) {
        return {
          ...entry,
          text: `${entry.text} - MOTION TABLED`,
          status: 'tabled',
          result: 'tabled',
          motionData: tabledMotion,
          effectType: 'table',
          lastUpdated: Date.now()
        };
      }
      // Update the Table motion itself to show it passed
      // Keep its original parentMotionId (don't change nesting)
      if (entry.id === `motion_${motion.id}`) {
        return {
          ...entry,
          status: 'completed',
          result: 'passed',
          type: 'motion_completed',
          // Keep original parentMotionId - don't change it
          lastUpdated: Date.now()
        };
      }
      return entry;
    });

    return {
      ...meetingState,
      currentMotion: null,
      motionStack: [],
      tabledMotions: [...tabledMotions, tabledMotion],
      meetingMinutes: updatedMinutes,
      advanceToNextBusiness: true // Signal to advance agenda
    };
  }
};

/**
 * Postpone to Certain Time Effects
 * RONR: Removes from current business and schedules for specified time
 */
const postponeMotionEffects = {
  execute: (motion, meetingState) => {
    console.log('⏰ Postpone motion passed - scheduling for later');

    const { currentMotion, motionStack, meetingMinutes, postponedMotions = [] } = meetingState;

    const motionToPostpone = currentMotion?.type === 'MAIN' ? currentMotion :
                            motionStack.find(m => m.type === 'MAIN');

    if (!motionToPostpone) {
      console.warn('Postpone motion passed but no business to postpone');
      return meetingState;
    }

    const postponeTime = extractPostponeTime(motion.text);

    const postponedMotion = {
      ...motionToPostpone,
      status: 'postponed',
      postponedAt: new Date().toLocaleTimeString(),
      postponedTo: postponeTime,
      originalStack: motionStack.filter(m => m.type !== 'POSTPONE'),
      result: 'postponed',
      id: motionToPostpone.id || Date.now()
    };

    const mainLineText = `Motion "${motionToPostpone.text}" postponed to ${postponeTime}.`;

    const updatedMinutes = meetingMinutes
      .filter(
        (e) => !(e.type === 'motion_effect' && e.effectType === 'postpone')
      )
      .map((entry) => {
        if (entry.id === `motion_${motionToPostpone.id}`) {
          return {
            ...entry,
            text: mainLineText,
            type: 'motion_completed',
            status: 'completed',
            result: 'postponed',
            motionData: postponedMotion,
            effectType: 'postpone',
            lastUpdated: Date.now()
          };
        }
        if (entry.id === `motion_${motion.id}`) {
          return {
            ...entry,
            status: 'completed',
            result: 'passed',
            type: 'motion_completed',
            lastUpdated: Date.now()
          };
        }
        return entry;
      });

    return {
      ...meetingState,
      currentMotion: null,
      motionStack: [],
      postponedMotions: [...postponedMotions, postponedMotion],
      meetingMinutes: updatedMinutes,
      advanceToNextBusiness: true
    };
  }
};

/**
 * Refer to Committee Effects
 * RONR: Removes from current business and sends to specified committee
 */
const referToCommitteeEffects = {
  execute: (motion, meetingState) => {
    console.log('🏛️ Refer to Committee passed - sending to committee');

    const { currentMotion, motionStack, meetingMinutes, referredMotions = [] } = meetingState;

    const motionToRefer = currentMotion?.type === 'MAIN' ? currentMotion :
                         motionStack.find(m => m.type === 'MAIN');

    if (!motionToRefer) {
      console.warn('Refer motion passed but no business to refer');
      return meetingState;
    }

    const committeeInfo = extractCommitteeInfo(motion.text);

    const referredMotion = {
      ...motionToRefer,
      status: 'referred',
      referredAt: new Date().toLocaleTimeString(),
      referredTo: committeeInfo.committee,
      referralInstructions: committeeInfo.instructions,
      originalStack: motionStack.filter(m => m.type !== 'REFER'),
      id: motionToRefer.id || Date.now()
    };

    const newMinute = {
      id: Date.now() + '_referred',
      type: 'motion_effect',
      text: `Motion referred to ${committeeInfo.committee}: "${motionToRefer.text}"`,
      timestamp: new Date().toLocaleTimeString(),
      chronologicalOrder: (meetingMinutes[meetingMinutes.length - 1]?.chronologicalOrder || 0) + 1,
      effectType: 'refer',
      motionData: referredMotion,
      status: 'completed'
    };

    return {
      ...meetingState,
      currentMotion: null,
      motionStack: [],
      referredMotions: [...referredMotions, referredMotion],
      meetingMinutes: [...meetingMinutes, newMinute],
      advanceToNextBusiness: true
    };
  }
};

/**
 * Close Debate Effects (alternative to Previous Question)
 */
const closeDebateEffects = {
  execute: (motion, meetingState) => {
    return previousQuestionEffects.execute(motion, meetingState);
  }
};

/**
 * Limit Debate Effects
 * RONR: Sets time limits on debate
 */
const limitDebateEffects = {
  execute: (motion, meetingState) => {
    console.log('⏱️ Limit Debate passed - setting debate time limits');

    const timeLimit = extractTimeLimit(motion.text);
    const { meetingMinutes } = meetingState;

    const newMinute = {
      id: Date.now() + '_limit_debate',
      type: 'motion_effect',
      text: `Debate limited to ${timeLimit}`,
      timestamp: new Date().toLocaleTimeString(),
      chronologicalOrder: (meetingMinutes[meetingMinutes.length - 1]?.chronologicalOrder || 0) + 1,
      effectType: 'limit_debate',
      status: 'completed'
    };

    return {
      ...meetingState,
      debateTimeLimit: timeLimit,
      debateLimitSet: true,
      meetingMinutes: [...meetingMinutes, newMinute]
    };
  }
};

/**
 * Utility Functions
 */

const extractPostponeTime = (motionText) => {
  // Try to extract time from motion text like "postpone to next meeting" or "postpone to 3:00 PM"
  const timeMatch = motionText.match(/postpone.*?to\s+(.+)/i);
  return timeMatch ? timeMatch[1].trim() : 'specified time';
};

const extractCommitteeInfo = (motionText) => {
  // Try to extract committee name from motion text
  const committeeMatch = motionText.match(/refer.*?to\s+(.+?)(?:\s+with|$)/i);
  const committee = committeeMatch ? committeeMatch[1].trim() : 'specified committee';

  const instructionsMatch = motionText.match(/with\s+instructions?\s+(.+)/i);
  const instructions = instructionsMatch ? instructionsMatch[1].trim() : null;

  return { committee, instructions };
};

const extractTimeLimit = (motionText) => {
  // Try to extract time limit from motion text
  const timeMatch = motionText.match(/(\d+)\s*(minutes?|hours?)/i);
  return timeMatch ? `${timeMatch[1]} ${timeMatch[2]}` : '10 minutes per speaker';
};

/**
 * Functions for retrieving disposed motions
 */

export const takeFromTable = (tabledMotionId, meetingState) => {
  const { tabledMotions = [], meetingMinutes } = meetingState;
  const motionToRestore = tabledMotions.find(m => m.id === tabledMotionId);

  if (!motionToRestore) {
    console.warn('Motion not found in tabled motions');
    return meetingState;
  }

  const restoredMotion = {
    ...motionToRestore,
    id: Date.now(), // Give restored motion a new ID to break nesting with old subsidiary motions
    originalId: motionToRestore.id, // Keep reference to original for tracking
    status: motionToRestore.status === 'tabled' ? 'open_debate' : motionToRestore.status,
    tabledAt: undefined,
    restoredAt: new Date().toLocaleTimeString()
  };

  const newMinute = {
    id: `motion_${restoredMotion.id}`, // Use new motion ID for clean entry
    type: 'motion_stated',
    text: `${restoredMotion.member} moved ${restoredMotion.type === 'MAIN' ? 'main motion' : 'motion'}: "${restoredMotion.text}" (taken from table)`,
    timestamp: new Date().toLocaleTimeString(),
    chronologicalOrder: (meetingMinutes[meetingMinutes.length - 1]?.chronologicalOrder || 0) + 1,
    status: 'pending',
    motionData: restoredMotion,
    parentMotionId: null // No parent - this is a fresh top-level motion now
  };

  return {
    ...meetingState,
    currentMotion: restoredMotion,
    motionStack: [restoredMotion], // Restored motion is clean, no subsidiary motions
    tabledMotions: tabledMotions.filter(m => m.id !== tabledMotionId),
    meetingMinutes: [...meetingMinutes, newMinute]
  };
};

export const resumePostponedMotion = (postponedMotionId, meetingState) => {
  const { postponedMotions = [], meetingMinutes } = meetingState;
  const motionToResume = postponedMotions.find(m => m.id === postponedMotionId);

  if (!motionToResume) {
    console.warn('Motion not found in postponed motions');
    return meetingState;
  }

  const resumedMotion = {
    ...motionToResume,
    status: 'stated',
    postponedAt: undefined,
    resumedAt: new Date().toLocaleTimeString()
  };

  const newMinute = {
    id: Date.now() + '_resumed',
    type: 'motion_effect',
    text: `Postponed motion resumed: "${resumedMotion.text}"`,
    timestamp: new Date().toLocaleTimeString(),
    chronologicalOrder: (meetingMinutes[meetingMinutes.length - 1]?.chronologicalOrder || 0) + 1,
    effectType: 'resume',
    motionData: resumedMotion,
    status: 'completed'
  };

  return {
    ...meetingState,
    currentMotion: resumedMotion,
    motionStack: motionToResume.originalStack || [resumedMotion],
    postponedMotions: postponedMotions.filter(m => m.id !== postponedMotionId),
    meetingMinutes: [...meetingMinutes, newMinute]
  };
};

/**
 * Postpone Indefinitely Effects
 * RONR: Kills the main motion without a direct vote on it
 */
const postponeIndefinitelyEffects = {
  execute: (motion, meetingState) => {
    console.log('❌ Postpone Indefinitely passed - main motion is killed');

    const { currentMotion, motionStack, meetingMinutes } = meetingState;

    const mainMotion = motionStack.find(m => MOTION_TYPES[m.type]?.class === MOTION_CLASSES.MAIN) ||
                      (currentMotion && MOTION_TYPES[currentMotion.type]?.class === MOTION_CLASSES.MAIN ? currentMotion : null);

    if (!mainMotion) {
      console.warn('Postpone Indefinitely passed but no main motion found');
      return meetingState;
    }

    const newMinute = {
      id: Date.now() + '_postponed_indef',
      type: 'motion_effect',
      text: `Main motion postponed indefinitely (killed): "${mainMotion.text}"`,
      timestamp: new Date().toLocaleTimeString(),
      chronologicalOrder: (meetingMinutes[meetingMinutes.length - 1]?.chronologicalOrder || 0) + 1,
      effectType: 'postpone_indefinitely',
      status: 'completed'
    };

    return {
      ...meetingState,
      currentMotion: null,
      motionStack: [],
      meetingMinutes: [...meetingMinutes, newMinute],
      advanceToNextBusiness: true
    };
  }
};

/**
 * Adjourn Effects
 * RONR: Ends the meeting
 */
const adjournEffects = {
  execute: (motion, meetingState) => {
    console.log('🔚 Adjourn passed - meeting ended');

    const { meetingMinutes } = meetingState;

    const newMinute = {
      id: Date.now() + '_adjourned',
      type: 'motion_effect',
      text: 'Meeting adjourned',
      timestamp: new Date().toLocaleTimeString(),
      chronologicalOrder: (meetingMinutes[meetingMinutes.length - 1]?.chronologicalOrder || 0) + 1,
      effectType: 'adjourn',
      status: 'completed'
    };

    return {
      ...meetingState,
      currentMotion: null,
      motionStack: [],
      meetingEndTime: new Date().toLocaleTimeString(),
      meetingMinutes: [...meetingMinutes, newMinute],
      meetingAdjourned: true
    };
  }
};

/**
 * Recess Effects
 * RONR: Suspends the meeting for specified time
 */
const recessEffects = {
  execute: (motion, meetingState) => {
    console.log('⏸️ Recess passed - meeting suspended');

    const { meetingMinutes } = meetingState;
    const recessTime = extractRecessTime(motion.text);

    const newMinute = {
      id: Date.now() + '_recess',
      type: 'motion_effect',
      text: `Meeting in recess for ${recessTime}`,
      timestamp: new Date().toLocaleTimeString(),
      chronologicalOrder: (meetingMinutes[meetingMinutes.length - 1]?.chronologicalOrder || 0) + 1,
      effectType: 'recess',
      status: 'completed'
    };

    return {
      ...meetingState,
      meetingMinutes: [...meetingMinutes, newMinute],
      inRecess: true,
      recessTime: recessTime,
      recessStarted: new Date().toLocaleTimeString()
    };
  }
};

/**
 * Withdraw Motion Effects
 * RONR: Removes motion from consideration
 */
const withdrawMotionEffects = {
  execute: (motion, meetingState) => {
    console.log('↩️ Motion withdrawn');

    const { currentMotion, motionStack, meetingMinutes } = meetingState;

    if (!currentMotion) {
      return meetingState;
    }

    const newMinute = {
      id: Date.now() + '_withdrawn',
      type: 'motion_effect',
      text: `Motion withdrawn: "${currentMotion.text}"`,
      timestamp: new Date().toLocaleTimeString(),
      chronologicalOrder: (meetingMinutes[meetingMinutes.length - 1]?.chronologicalOrder || 0) + 1,
      effectType: 'withdraw',
      status: 'completed'
    };

    // Remove current motion and return to previous motion on stack
    const newStack = motionStack.filter(m => m.id !== currentMotion.id);
    const newCurrentMotion = newStack.length > 0 ? newStack[newStack.length - 1] : null;
    const updatedStack = newCurrentMotion ? newStack.filter(m => m.id !== newCurrentMotion.id) : [];

    return {
      ...meetingState,
      currentMotion: newCurrentMotion,
      motionStack: updatedStack,
      meetingMinutes: [...meetingMinutes, newMinute]
    };
  }
};

/**
 * Division of Question Effects
 * RONR: Separates a motion into parts
 */
const divisionQuestionEffects = {
  execute: (motion, meetingState) => {
    console.log('✂️ Division of Question passed - separating motion');

    const { meetingMinutes } = meetingState;

    const newMinute = {
      id: Date.now() + '_division',
      type: 'motion_effect',
      text: 'Motion divided into separate questions for individual consideration',
      timestamp: new Date().toLocaleTimeString(),
      chronologicalOrder: (meetingMinutes[meetingMinutes.length - 1]?.chronologicalOrder || 0) + 1,
      effectType: 'division_question',
      status: 'completed'
    };

    return {
      ...meetingState,
      meetingMinutes: [...meetingMinutes, newMinute],
      needsQuestionDivision: true // Signal to UI to handle division
    };
  }
};

/**
 * Suspend Rules Effects
 * RONR: Temporarily sets aside rules
 */
const suspendRulesEffects = {
  execute: (motion, meetingState) => {
    console.log('⚠️ Rules suspended');

    const { meetingMinutes } = meetingState;
    const suspendedRules = extractSuspendedRules(motion.text);

    const newMinute = {
      id: Date.now() + '_suspend_rules',
      type: 'motion_effect',
      text: `Rules suspended: ${suspendedRules}`,
      timestamp: new Date().toLocaleTimeString(),
      chronologicalOrder: (meetingMinutes[meetingMinutes.length - 1]?.chronologicalOrder || 0) + 1,
      effectType: 'suspend_rules',
      status: 'completed'
    };

    return {
      ...meetingState,
      meetingMinutes: [...meetingMinutes, newMinute],
      suspendedRules: suspendedRules,
      rulesSuspended: true
    };
  }
};

/**
 * Take from the Table Effects (bring-back motion)
 * RONR: resumes consideration of the motion most recently laid on the table.
 */
const takeFromTableEffects = {
  execute: (motion, meetingState) => {
    console.log('📥 Take from the Table passed - restoring tabled business');

    const { tabledMotions = [], meetingMinutes } = meetingState;

    if (tabledMotions.length === 0) {
      const newMinute = {
        id: Date.now() + '_take_from_table_none',
        type: 'motion_effect',
        text: 'Motion to take from the table adopted, but no motion is currently on the table.',
        timestamp: new Date().toLocaleTimeString(),
        chronologicalOrder: (meetingMinutes[meetingMinutes.length - 1]?.chronologicalOrder || 0) + 1,
        effectType: 'take_from_table',
        status: 'completed'
      };
      return { ...meetingState, meetingMinutes: [...meetingMinutes, newMinute] };
    }

    // Restore the most recently tabled motion and keep it on the floor.
    const target = tabledMotions[tabledMotions.length - 1];
    const restoredState = takeFromTable(target.id, meetingState);
    return { ...restoredState, advanceToNextBusiness: true };
  }
};

/**
 * Reconsider Effects (bring-back motion)
 * RONR: reopens for a fresh vote a question decided earlier in the same session.
 * Note: the app records the action; re-establishing the original question on the
 * floor for the re-vote is left to the chair.
 */
const reconsiderEffects = {
  execute: (motion, meetingState) => {
    console.log('🔁 Reconsider passed - reopening the earlier decision');

    const { meetingMinutes } = meetingState;

    const newMinute = {
      id: Date.now() + '_reconsider',
      type: 'motion_effect',
      text: `Motion to reconsider adopted: "${motion.text}". The question is again before the assembly.`,
      timestamp: new Date().toLocaleTimeString(),
      chronologicalOrder: (meetingMinutes[meetingMinutes.length - 1]?.chronologicalOrder || 0) + 1,
      effectType: 'reconsider',
      status: 'completed'
    };

    return { ...meetingState, meetingMinutes: [...meetingMinutes, newMinute] };
  }
};

/**
 * Rescind / Amend Something Previously Adopted Effects (bring-back motion)
 */
const rescindEffects = {
  execute: (motion, meetingState) => {
    console.log('🗑️ Rescind / Amend Something Previously Adopted passed');

    const { meetingMinutes } = meetingState;

    const newMinute = {
      id: Date.now() + '_rescind',
      type: 'motion_effect',
      text: `Previously adopted action rescinded / amended: "${motion.text}".`,
      timestamp: new Date().toLocaleTimeString(),
      chronologicalOrder: (meetingMinutes[meetingMinutes.length - 1]?.chronologicalOrder || 0) + 1,
      effectType: 'rescind',
      status: 'completed'
    };

    return { ...meetingState, meetingMinutes: [...meetingMinutes, newMinute] };
  }
};

/**
 * Ratify Effects (bring-back motion)
 */
const ratifyEffects = {
  execute: (motion, meetingState) => {
    console.log('✅ Ratify passed - confirming prior action');

    const { meetingMinutes } = meetingState;

    const newMinute = {
      id: Date.now() + '_ratify',
      type: 'motion_effect',
      text: `Action ratified: "${motion.text}".`,
      timestamp: new Date().toLocaleTimeString(),
      chronologicalOrder: (meetingMinutes[meetingMinutes.length - 1]?.chronologicalOrder || 0) + 1,
      effectType: 'ratify',
      status: 'completed'
    };

    return { ...meetingState, meetingMinutes: [...meetingMinutes, newMinute] };
  }
};

/**
 * Additional Utility Functions
 */
const extractRecessTime = (motionText) => {
  const timeMatch = motionText.match(/recess.*?for\s+(.+)/i);
  return timeMatch ? timeMatch[1].trim() : '10 minutes';
};

const extractSuspendedRules = (motionText) => {
  const rulesMatch = motionText.match(/suspend.*?rules.*?that\s+(.+)/i);
  return rulesMatch ? rulesMatch[1].trim() : 'specified rules';
};

/**
 * Check if a motion type has automatic effects
 */
export const hasAutomaticEffects = (motionType) => {
  return [
    'PREVQ', 'TABLE', 'POSTPONE', 'COMMIT', 'POSTPONE_INDEF', 'LIMIT_DEBATE',
    'ADJ', 'RECESS', 'WITHDRAW_MOTION', 'DIVISION_QUESTION', 'SUSPEND_RULES',
    'TAKE_FROM_TABLE', 'RECONSIDER', 'RESCIND', 'RATIFY'
  ].includes(motionType);
};

/**
 * Get description of what will happen when a motion passes
 */
export const getMotionEffectDescription = (motionType) => {
  const descriptions = {
    'PREVQ': 'Will immediately close debate and advance to vote',
    'TABLE': 'Will dispose of pending business until taken from table',
    'POSTPONE': 'Will remove from current business and schedule for specified time',
    'COMMIT': 'Will send to committee and remove from current business',
    'POSTPONE_INDEF': 'Will kill the main motion without direct vote',
    'LIMIT_DEBATE': 'Will set time limits on remaining debate',
    'ADJ': 'Will end the meeting',
    'RECESS': 'Will suspend the meeting for specified time',
    'WITHDRAW_MOTION': 'Will remove motion from consideration',
    'DIVISION_QUESTION': 'Will separate motion into parts for individual votes',
    'SUSPEND_RULES': 'Will temporarily set aside specified rules',
    'TAKE_FROM_TABLE': 'Will bring the most recently tabled motion back to the floor',
    'RECONSIDER': 'Will reopen the earlier decision for a fresh vote',
    'RESCIND': 'Will repeal or amend the previously adopted motion',
    'RATIFY': 'Will confirm and validate action already taken'
  };

  return descriptions[motionType] || null;
};